
import { useState, useEffect } from 'react';
import { groupBadgesApi } from '../lib/api';

export interface GroupBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  requirement: string;
}

export const GROUP_BADGES: GroupBadge[] = [
  {
    id: 'founding_member',
    name: 'Founding Member',
    description: 'One of the first members to join this group',
    icon: 'ri-medal-line',
    color: 'text-yellow-600 bg-yellow-50',
    requirement: 'Join within first 10 members'
  },
  {
    id: 'top_contributor',
    name: 'Top Contributor',
    description: 'Consistently creates valuable content',
    icon: 'ri-star-line',
    color: 'text-purple-600 bg-purple-50',
    requirement: '50+ posts with high engagement'
  },
  {
    id: 'active_participant',
    name: 'Active Participant',
    description: 'Regularly engages with the community',
    icon: 'ri-fire-line',
    color: 'text-orange-600 bg-orange-50',
    requirement: '100+ comments or reactions'
  },
  {
    id: 'group_veteran',
    name: 'Group Veteran',
    description: 'Long-standing member of the community',
    icon: 'ri-shield-line',
    color: 'text-blue-600 bg-blue-50',
    requirement: 'Member for 6+ months'
  },
  {
    id: 'helpful_guide',
    name: 'Helpful Guide',
    description: 'Helps new members and answers questions',
    icon: 'ri-question-answer-line',
    color: 'text-green-600 bg-green-50',
    requirement: '25+ helpful responses'
  },
  {
    id: 'content_creator',
    name: 'Content Creator',
    description: 'Creates original and engaging content',
    icon: 'ri-quill-pen-line',
    color: 'text-pink-600 bg-pink-50',
    requirement: '20+ original posts'
  }
];

export function useGroupBadges(groupId: string, userId: string | undefined) {
  const [badges, setBadges] = useState<string[]>([]);
  const [level, setLevel] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !groupId) {
      setLoading(false);
      return;
    }

    fetchBadgesAndLevel();
  }, [groupId, userId]);

  const fetchBadgesAndLevel = async () => {
    if (!userId) return;

    try {
      // Fetch badges and level via API
      const response = await groupBadgesApi.getMemberBadges(groupId, userId);

      if (response.success && response.data) {
        setBadges(response.data.badges || []);
        setLevel(response.data.level || 1);
      }
    } catch (error) {
      console.error('Error fetching badges and level:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAndAwardBadges = async () => {
    if (!userId) return;

    try {
      // Check and award badges via API
      const response = await groupBadgesApi.checkAndAwardBadges(groupId);

      if (response.success && response.data) {
        setBadges(response.data.badges || badges);
        setLevel(response.data.level || level);
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  };

  return {
    badges,
    level,
    loading,
    checkAndAwardBadges,
    GROUP_BADGES
  };
}
